/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, alpha, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import SearchInput from '../../components/inputs/searchInput';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import debounce from 'lodash.debounce';
import Popup from '../../components/popups/popup';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import UserService from '../../services/userService';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import RoleDropdown from '../../components/dropdowns/roleDropdown';
import StatusDropdown from '../../components/dropdowns/statusDropdown';
import ViewUserModal from '../../components/modals/user/viewUserModal';
import DeleteUserModal from '../../components/modals/user/deleteUserModal';
import { Link } from 'react-router-dom';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';
import UserTypeDropdown from '../../components/dropdowns/userTypeDropdown';

const Users = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (user) => {
        setSelectedUser(user);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (user) => {
        setSelectedUser(user);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedBranch(null);
            setSelectedRole(null);
            setSelectedStatus('');
            setSelectedType('');
            await fetchUsers();
        }
    };

    const handleStatus = (status) => {
        if (status === t("active")) return "active";
        else if (status === t("inactive")) return "inactive";
    };

    const handleType = (value) => {
        if (value === t("user")) return "user";
        else if (value === t("supervisor")) return "supervisor";
    };

    const fetchUsers = async (page = 1, name = '', branchId = null, roleId = null, status = '', type = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                name, branchId, roleId,
                status: handleStatus(status),
                type: handleType(type),
                ...sortState, deleted
            };
            const data = await UserService.fetch(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setUsers([]);
                }
            } else {
                setUsers((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchUsers = useMemo(() =>
        debounce((name, branchId, roleId, status) => {
            setPage(1);
            setHasMore(true);
            fetchUsers(1, name, branchId, roleId, status);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchUsers(value, selectedBranch?.id, selectedRole?.id, selectedStatus, selectedType);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setSelectedBranch(null);
        setSelectedRole(null);
        setSelectedStatus('');
        setSelectedType('');
        if (page !== 1) setPage(1);
        else fetchUsers(1, '');
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchUsers(page, searchTerm, selectedBranch?.id, selectedRole?.id, selectedStatus, selectedType);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchUsers(1, searchTerm, selectedBranch?.id, selectedRole?.id, selectedStatus, selectedType);
    }, [selectedBranch, selectedRole, selectedStatus, selectedType, sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchUsers(page + 1, searchTerm, selectedBranch?.id, selectedRole?.id, selectedStatus, selectedType);
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteUserModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} user={selectedUser} />
                <ViewUserModal open={openView} handleClose={handleCloseView} userId={selectedUser.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        my: 2,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,

                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'90%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                    </Box>

                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <BranchDropdown
                            isFilter={true}
                            selectedBranch={selectedBranch}
                            setSelectedBranch={setSelectedBranch}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <RoleDropdown
                            isFilter={true}
                            selectedRole={selectedRole}
                            setSelectedRole={setSelectedRole}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <StatusDropdown
                            isFilter={true}
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <UserTypeDropdown
                            isFilter={true}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{
                            ml: isEn ? 3 : 0
                        }}>
                            <OnOffSwitchForDeleted
                                checkDeleted={deleted}
                                setCheckDeleted={setDeleted} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>



                </Box>
                    :
                    <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <RoleDropdown
                                isFilter={true}
                                selectedRole={selectedRole}
                                setSelectedRole={setSelectedRole}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <StatusDropdown
                                isFilter={true}
                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <UserTypeDropdown
                                isFilter={true}
                                selectedType={selectedType}
                                setSelectedType={setSelectedType}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 1,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={2}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 170px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('name')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('name')}
                                    {sortState.sortBy === 'name' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('branch')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('branch')}
                                    {sortState.sortBy === 'branch' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('type')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('type')}
                                    {sortState.sortBy === 'type' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('role')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('role')}
                                    {sortState.sortBy === 'role' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('status')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('status')}
                                    {sortState.sortBy === 'status' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user, index) => (
                            <TableRow
                                key={user.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{user.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{user.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{user.branch.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{t(user.type)}</TableCell>
                                {/* role */}
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        fontWeight: 'bold',
                                        color: user.role.superAdmin ? theme.palette.green.box : theme.palette.purple.box,
                                    }}>
                                        {user.role.name}</Box>
                                </TableCell>
                                {/* status */}
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box
                                        sx={{
                                            bgcolor: alpha(
                                                user.status === 'active' ? theme.palette.green.box : theme.palette.red.box, 0.2),
                                            width: 100,
                                            justifyContent: 'center',
                                            display: 'flex',
                                            py: 0.8,
                                            borderRadius: 0.8,
                                        }}
                                    >
                                        <Box sx={{
                                            fontWeight: 'bold',
                                            color: user.status === 'active' ? theme.palette.green.box : theme.palette.red.box,
                                        }}>
                                            {t(user.status)}</Box>
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(user)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        {user.deletedAt === null && <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: user.role.superAdmin ? 'not-allowed' : 'pointer',
                                            pointerEvents: user.role.superAdmin ? 'none' : 'auto',
                                            opacity: user.role.superAdmin ? 0.5 : 1,
                                        }}
                                            component={Link}
                                            to={`/users/${user.id}`}
                                        >
                                            <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>}
                                        {user.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: user.role.superAdmin ? 'not-allowed' : 'pointer',
                                            pointerEvents: user.role.superAdmin ? 'none' : 'auto',
                                            opacity: user.role.superAdmin ? 0.5 : 1,
                                        }} onClick={!user.role.superAdmin ? () => handleOpenDelete(user) : undefined}>
                                            {user.deletedAt === null ?
                                                <DeleteOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.red.bg }} />
                                                :
                                                <ReplayOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.green.bg }} />
                                            }
                                        </Box>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Users;
